// The Scope PA tab (#515, MJ 23 Oct 2026: "for ease of review on longer costings"): the template's
// Scope sheet — "Schedule of Work", areas down (Interior, then Exterior), contract years across
// (the calendar year over its number), each cell R Repaint · M Service · – No Work (W Wash, not in
// the template), and the Payments per Annum (PAYG) under it.
import type { YearWork } from '@higgins/m2q-pricing';
import type { MaintenancePricing } from '../lib/maintenance-pricing';
import { formatSell } from './surface-format';

const WORK_LETTER: Record<YearWork, string> = { repaint: 'R', service: 'M', wash: 'W', none: '–' };
/** The template's two groups; an area is Exterior only when it is on the external side (#510's rule). */
const EXTERIOR_SIDE = 'External';
const GROUPS = [
  { label: 'Interior', exterior: false },
  { label: 'Exterior', exterior: true },
];
const CELL = 'border-l border-slate-200 px-2 py-1 text-center tabular-nums';

interface Props {
  pricing: MaintenancePricing;
  /** Each area's side, by area id (SectionInfo.side). */
  sides: ReadonlyMap<string, string | null>;
  priceShown: boolean;
}

export function ScopePaTab({ pricing, sides, priceShown }: Props) {
  const { contract, yearly } = pricing;
  const width = yearly.years.length + 1;
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead>
            <tr>
              <th colSpan={width} className="px-2 py-1.5 text-left font-semibold text-slate-900">
                Schedule of Work
              </th>
            </tr>
            <tr className="text-xs text-slate-500">
              <th className="px-2 text-left font-normal">Area</th>
              {yearly.years.map((y) => (
                <th key={y.number} className={`${CELL} font-semibold text-slate-800`}>
                  {y.label}
                </th>
              ))}
            </tr>
            <tr className="text-xs text-slate-500">
              <th />
              {yearly.years.map((y) => (
                <th key={y.number} className={`${CELL} font-normal`}>
                  {y.number}
                </th>
              ))}
            </tr>
          </thead>
          {GROUPS.map((group) => {
            const areas = contract.sections.filter((s) => (sides.get(s.id) === EXTERIOR_SIDE) === group.exterior);
            if (!areas.length) return null;
            return (
              <tbody key={group.label}>
                <tr className="bg-slate-50">
                  <td colSpan={width} className="px-2 py-1 font-semibold text-slate-800">
                    {group.label}
                  </td>
                </tr>
                {areas.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100">
                    <td className="px-2 py-1">{s.name}</td>
                    {s.programme.map((work, i) => (
                      <td key={i} className={`${CELL} ${work === 'none' ? 'text-slate-400' : 'font-medium'}`}>
                        {WORK_LETTER[work]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            );
          })}
          <tbody>
            <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
              <td className="px-2 py-1">Payments per Annum <span className="text-xs font-normal text-slate-500">({yearly.paymentType})</span></td>
              {yearly.years.map((y) => (
                <td key={y.number} className={`${CELL} whitespace-nowrap`}>
                  {formatSell(y.sell, priceShown)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <p className="max-w-3xl text-xs text-slate-500">
        The template&rsquo;s Scope sheet: R Repaint · M Service · W Wash · – No Work, from each area&rsquo;s Schedule of Work on the Take Off. Payments per Annum are the
        Yearly Summaries&rsquo; totals.
      </p>
    </div>
  );
}
